import { api } from '../api/client'
import type { FunnelDefinition, FunnelHistoryPoint, FunnelReportResponse } from '../api/types'
import { useAsync } from '../api/useReport'
import { Banner } from '../components/Banner'
import { SkeletonPage, SkeletonTable, SkeletonTiles } from '../components/Skeleton'
import { FunnelReport } from '../components/FunnelReport'
import { formatDate } from '../format'

interface Props {
  /** Null when the section was entered from the nav rather than from a named funnel. */
  funnelName: string | null
  reportDate: string
  maxDate: string
  propertyId?: string
  onBack: () => void
  onFunnelChange: (name: string) => void
  onDateChange: (reportDate: string) => void
}

/**
 * One funnel, on one day.
 *
 * Deliberately day-shaped rather than windowed, because that is what the analysis
 * computes: `analyse_funnel` compares a report date against the same weekday a week
 * earlier and its step counts are filtered to a single day. Offering a 7/28-day
 * selector here would promise an aggregation the backend does not perform.
 *
 * The strip along the top is therefore context rather than a range control — recent
 * end-to-end rates read out of the daybook, where each point was recorded on the day
 * it was first built rather than recomputed now.
 */
export function FunnelView({
  funnelName,
  reportDate,
  maxDate,
  propertyId,
  onBack,
  onFunnelChange,
  onDateChange,
}: Props) {
  const definitions = useAsync<FunnelDefinition[]>(
    (signal) => api.funnels({ propertyId }, signal),
    [propertyId],
    true,
    `funnels:${propertyId ?? ''}`,
  )

  const configured = (definitions.data ?? []).filter((item) => item.enabled)
  // Arriving from the nav names no funnel, so the first configured one leads. Resolved
  // here rather than in the shell, which would have to fetch the definitions to know.
  const effective = funnelName ?? configured[0]?.name ?? null

  const { data, error, loading } = useAsync<FunnelReportResponse>(
    (signal) => api.funnelReport(effective as string, { reportDate, propertyId }, signal),
    [effective, reportDate, propertyId],
    effective !== null,
    `funnel:${propertyId ?? ''}:${effective ?? ''}:${reportDate}`,
  )

  const previous = shiftDate(reportDate, -1)
  const next = shiftDate(reportDate, 1)

  return (
    <>
      <div className="context-bar">
        <div className="context-bar__left">
          <button type="button" className="link-button link-button--back" onClick={onBack}>
            ← Back to {formatDate(reportDate, 'long')}
          </button>

          {configured.length > 1 && (
            <label className="field field--inline">
              <span className="visually-hidden">Funnel</span>
              <select
                value={effective ?? ''}
                onChange={(event) => onFunnelChange(event.target.value)}
              >
                {configured.map((item) => (
                  <option key={item.name} value={item.name}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="context-bar__right">
          <div className="stepper">
            <button
              type="button"
              className="stepper__button"
              onClick={() => onDateChange(previous)}
              aria-label="Previous day"
            >
              ←
            </button>
            <span className="stepper__label">{formatDate(reportDate, 'long')}</span>
            <button
              type="button"
              className="stepper__button"
              onClick={() => onDateChange(next)}
              // A day GA4 has not settled yet cannot be reported on, so the control
              // stops at the last settled day rather than offering an empty answer.
              disabled={next > maxDate}
              aria-label="Next day"
            >
              →
            </button>
          </div>
          {data?.funnel.baseline_label && (
            <span className="section__note">vs {data.funnel.baseline_label}</span>
          )}
        </div>
      </div>

      {error && <Banner tone="critical">{error}</Banner>}
      {loading && (
        <SkeletonPage label="Loading the funnel…">
          <SkeletonTiles count={4} />
          <SkeletonTable rows={4} columns={7} />
        </SkeletonPage>
      )}
      {!definitions.loading && configured.length === 0 && (
        <div className="card">
          <p className="placeholder">
            No funnels are configured. Add one on the Settings tab — a funnel needs at least
            two ordered events before there is a path to report.
          </p>
        </div>
      )}

      {data && !loading && (
        <>
          <History points={data.history} funnelLabel={data.funnel.label} />
          <FunnelReport funnel={data.funnel} />
        </>
      )}
    </>
  )
}

/**
 * End-to-end rate across the recorded days, oldest to newest.
 *
 * Bars rather than a line: these are separate days that may not be contiguous — a day
 * nobody recorded is simply absent — and a line would draw a slope across the gap as
 * if it had measured something there.
 */
function History({ points, funnelLabel }: { points: FunnelHistoryPoint[]; funnelLabel: string }) {
  if (points.length < 2) {
    return (
      <p className="section__note funnel-history__empty">
        Recent rates appear here as days are recorded. {funnelLabel} has{' '}
        {points.length === 0 ? 'no' : 'one'} recorded {points.length === 1 ? 'day' : 'days'} so far —
        the daybook’s backfill fills this in.
      </p>
    )
  }

  const highest = Math.max(...points.map((point) => point.overall_rate_percent), 0.1)
  const latest = points[points.length - 1]

  return (
    <section className="funnel-history">
      <div className="funnel-history__head">
        <span className="funnel-history__label">End-to-end rate</span>
        <span className="section__note">
          {points.length} recorded {points.length === 1 ? 'day' : 'days'}, oldest first
        </span>
      </div>
      <div className="funnel-history__bars">
        {points.map((point) => {
          const isLatest = point.date === latest.date
          return (
            <span
              key={point.date}
              className={`funnel-history__bar${isLatest ? ' funnel-history__bar--latest' : ''}`}
              style={{ height: `${Math.max((point.overall_rate_percent / highest) * 100, 2)}%` }}
              title={`${formatDate(point.date, 'long')} · ${point.overall_rate_percent.toFixed(1)}%`}
            />
          )
        })}
      </div>
      <div className="funnel-history__axis">
        <span>{formatDate(points[0].date)}</span>
        <span>
          {latest.overall_rate_percent.toFixed(1)}% on {formatDate(latest.date)}
        </span>
      </div>
    </section>
  )
}

/**
 * Move an ISO date by whole days without going through local time.
 *
 * `new Date('2026-08-21')` parses as UTC midnight; adding a day and formatting it
 * locally returns the 21st again for anyone west of Greenwich.
 */
function shiftDate(iso: string, delta: number): string {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  const moved = new Date(Date.UTC(year, month - 1, day + delta))
  return moved.toISOString().slice(0, 10)
}
